import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import api from '../utils/api';
import { RefreshCw, QrCode } from 'lucide-react';

const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return { deviceName: "iPhone", deviceType: "mobile" };
  if (/iPad/.test(ua)) return { deviceName: "iPad", deviceType: "tablet" };
  if (/Android/.test(ua)) return /Mobile/.test(ua)
    ? { deviceName: "Android Phone", deviceType: "mobile" }
    : { deviceName: "Android Tablet", deviceType: "tablet" };
  if (/Macintosh/.test(ua)) return { deviceName: "Mac", deviceType: "desktop" };
  if (/Windows/.test(ua)) return { deviceName: "Windows PC", deviceType: "desktop" };
  return { deviceName: "Unknown Device", deviceType: "unknown" };
};

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Desktop QR session
  const [qrToken, setQrToken] = useState(null);
  const [qrExpired, setQrExpired] = useState(false);
  const pollRef = useRef(null);
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const generateQR = useCallback(async () => {
    try {
      setQrExpired(false);
      const res = await api.post('/auth/qr-generate');
      setQrToken(res.data.token);
    } catch { /* QR is optional enhancement */ }
  }, []);

  useEffect(() => { generateQR(); }, [generateQR]);

  // Poll for phone approval
  useEffect(() => {
    if (!qrToken) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/auth/qr-check?token=${qrToken}`);
        if (res.data.status === 'approved') {
          clearInterval(pollRef.current);
          if (res.data.session_id) localStorage.setItem('session_id', res.data.session_id);
          toast.success('Logged in via QR!');
          onLogin(res.data.user, res.data.access_token);
        } else if (res.data.status === 'expired') {
          clearInterval(pollRef.current);
          setQrExpired(true);
        }
      } catch { /* ignore */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [qrToken, onLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { deviceName, deviceType } = getDeviceInfo();
      const response = await api.post('/auth/login', {
        email, password, device_name: deviceName, device_type: deviceType,
      });
      if (response.data.session_id) localStorage.setItem('session_id', response.data.session_id);
      toast.success('Login successful!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // QR encodes a URL the phone opens — phone will read session token and approve it
  const qrUrl = qrToken ? `${baseUrl}/qr-approve?session=${qrToken}` : '';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 relative" style={{ background: 'var(--bg-void)' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-10" style={{ background: 'var(--amber)', filter: 'blur(120px)' }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/workforce-logo.png" alt="WorkForce" className="mx-auto" style={{ height: '108px', width: 'auto' }} />
        </div>

        <div className="rounded-xl p-6 sm:p-8" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-semibold tracking-wider uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>Email</label>
              <Input
                id="email" type="email" placeholder="username@company.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                className="h-11 px-4 rounded-lg text-sm border focus:outline-none focus:ring-2"
                style={{ background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', '--tw-ring-color': 'var(--amber)' }}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold tracking-wider uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>Password</label>
              <Input
                id="password" type="password" placeholder="Enter password"
                value={password} onChange={(e) => setPassword(e.target.value)} required
                className="h-11 px-4 rounded-lg text-sm border focus:outline-none focus:ring-2"
                style={{ background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', '--tw-ring-color': 'var(--amber)' }}
                data-testid="login-password-input"
              />
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs font-medium hover:underline" style={{ color: 'var(--amber)' }}>
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base font-bold tracking-wider uppercase rounded-lg transition-all hover:brightness-110"
              style={{ background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)', letterSpacing: '0.1em' }}
              disabled={loading} data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px" style={{ background: 'var(--border-color)' }} />
            <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>or scan with phone</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border-color)' }} />
          </div>

          {/* QR Code for desktop session */}
          <div className="flex flex-col items-center gap-3">
            {qrToken && !qrExpired ? (
              <>
                <div className="p-3 rounded-xl" style={{ background: '#ffffff' }}>
                  <QRCodeSVG value={qrUrl} size={140} bgColor="#ffffff" fgColor="#0d1117" level="M" data-testid="qr-code" />
                </div>
                <p className="text-[11px] text-center" style={{ color: 'var(--text-muted)' }}>
                  Scan with your registered phone to sign in instantly on this screen.
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <QrCode className="h-8 w-8" style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {qrExpired ? 'QR expired' : 'Loading...'}
                </p>
                {qrExpired && (
                  <Button variant="ghost" size="sm" onClick={generateQR} className="text-xs" style={{ color: 'var(--amber)' }} data-testid="qr-refresh">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
